import { Package } from 'lucide-react'

import type { TenantBrandingPayload } from '@/api/branding'
import { PLATFORM_LOGO } from '@/lib/platform-brand'
import { cn } from '@/lib/utils'

export type BrandAssets = {
  logoLight?: string | null
  logoDark?: string | null
  iconLight?: string | null
  iconDark?: string | null
}

type Props = BrandAssets & {
  storeName: string
  /** Dar sidebar / mobil: ikon; geniş: tam logo */
  variant?: 'icon' | 'logo'
  isDark?: boolean
  className?: string
  imgClassName?: string
}

function pickLogo(
  assets: BrandAssets,
  isDark: boolean,
  mobile: boolean,
): string | null {
  if (mobile) {
    const icon = isDark ? assets.iconDark ?? assets.iconLight : assets.iconLight ?? assets.iconDark
    if (icon) return icon
  }

  const logo = isDark ? assets.logoDark ?? assets.logoLight : assets.logoLight ?? assets.logoDark
  if (logo) return logo

  // Tenant markası yoksa CodexFix platform logosu
  if (mobile) {
    return isDark ? PLATFORM_LOGO.verticalDark : PLATFORM_LOGO.verticalLight
  }

  return isDark ? PLATFORM_LOGO.horizontalDark : PLATFORM_LOGO.horizontalLight
}

export function brandAssetsFromBranding(
  branding: Pick<
    TenantBrandingPayload,
    'logo_url' | 'logo_url_dark' | 'logo_mobile_url' | 'logo_mobile_url_dark'
  >,
): BrandAssets {
  return {
    logoLight: branding.logo_url,
    logoDark: branding.logo_url_dark,
    iconLight: branding.logo_mobile_url,
    iconDark: branding.logo_mobile_url_dark,
  }
}

export function AdminBrandLogo({
  storeName,
  logoLight,
  logoDark,
  iconLight,
  iconDark,
  variant = 'logo',
  isDark = false,
  className,
  imgClassName,
}: Props) {
  const assets: BrandAssets = { logoLight, logoDark, iconLight, iconDark }
  const mobile = variant === 'icon'
  const src = pickLogo(assets, isDark, mobile)
  const name = storeName.trim() || 'Mağaza'
  const markLetter = name.charAt(0).toUpperCase()

  if (!src) {
    return (
      <div
        className={cn(
          'grid shrink-0 place-content-center rounded-xl bg-gradient-to-br from-mainColor to-darkMainColor shadow-glow',
          mobile ? 'size-10' : 'size-10',
          className,
        )}
        aria-hidden
      >
        <Package className="h-5 w-5 text-white" />
      </div>
    )
  }

  return (
    <span className={cn('inline-flex shrink-0 items-center justify-center', className)}>
      <img
        src={src}
        alt={name}
        className={cn(
          mobile ? 'size-10 rounded-xl object-contain' : 'h-9 max-w-[160px] object-contain',
          imgClassName,
        )}
        onError={(e) => {
          const el = e.currentTarget
          el.style.display = 'none'
          const fallback = el.nextElementSibling
          if (fallback instanceof HTMLElement) {
            fallback.style.display = 'grid'
          }
        }}
      />
      <span
        className="hidden size-10 place-content-center rounded-xl bg-gradient-to-br from-mainColor to-darkMainColor text-sm font-bold text-white shadow-glow"
        aria-hidden
      >
        {markLetter}
      </span>
    </span>
  )
}
